import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Pagination, Spinner, Alert, Button, ButtonGroup } from 'react-bootstrap';
import axios from 'axios';
import { API_BASE_URL } from '../utils/apiConfig';
import InlineNotice from '../components/InlineNotice';
import { useInlineNotice } from '../contexts/InlineNoticeContext';
import usePermissions from '../utils/usePermissions';
import CustomerModal from '../components/CustomerModal';
import ConfirmDialog from '../components/ConfirmDialog';
import ListSearchBar from '../components/ListSearchBar';
import useDebouncedValue from '../utils/useDebouncedValue';
import { getActiveSearchTerm, shouldTriggerSearchRequest } from '../utils/searchThreshold';
import '../styles/CustomerList.css';

const CustomerListPage = () => {
  const { can } = usePermissions('customers');
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showInlineNotice } = useInlineNotice();
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    column: 'name',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });
  
  // Get current user role
  const permissions = useMemo(() => ({
    canCreate: can('create'),
    canEdit: can('edit'),
    canDelete: can('delete')
  }), [can]);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Delete confirmation states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const activeSearchTerm = getActiveSearchTerm(debouncedSearch);
  const shouldTriggerSearch = shouldTriggerSearchRequest(debouncedSearch);


  const fetchCustomers = async (page = 1, searchTerm = '') => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/customers?page=${page}&limit=${pagination.limit}`, {
        params: searchTerm ? { search: searchTerm } : undefined,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const payload = response.data || {};
      const nextCustomers = Array.isArray(payload.customers)
        ? payload.customers
        : Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : [];
      const rawPagination = payload.pagination || payload.meta || {};
      const nextPagination = {
        page,
        limit: pagination.limit,
        total: rawPagination.total ?? nextCustomers.length,
        pages: 1,
        ...rawPagination
      };

      if (!nextPagination.pages && rawPagination.totalPages) {
        nextPagination.pages = rawPagination.totalPages;
      }

      setCustomers(nextCustomers);
      setPagination(nextPagination);
      setError(null);
    } catch (err) {
      console.error('Error fetching customers:', err);
      const errorMessage = err.response?.data?.error || err.message;
      setError(`Failed to load customers: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldTriggerSearch) {
      return;
    }
    setSelectedRows([]);
    fetchCustomers(1, activeSearchTerm);
  }, [activeSearchTerm, shouldTriggerSearch]);

  const handlePageChange = (page) => {
    fetchCustomers(page, activeSearchTerm);
  };

  const handleReload = async () => {
    setSelectedRows([]);
    await fetchCustomers(pagination.page, activeSearchTerm);
  };

  const handleCheckboxChange = (customerId) => {
    setSelectedRows(prevSelected => {
      if (prevSelected.includes(customerId)) {
        return prevSelected.filter(id => id !== customerId);
      } else {
        return [...prevSelected, customerId];
      }
    });
  };

  const handleSort = (column) => {
    setSortConfig(prevConfig => ({
      column,
      direction: prevConfig.column === column && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Add new customer
  const handleAddCustomer = () => {
    if (!permissions.canCreate) return;
    setSelectedCustomer(null);
    setShowModal(true);
  };

  // View selected customer
  const handleViewCustomer = () => {
    if (selectedRows.length === 1) {
      const customer = customers.find(c => c._id === selectedRows[0]);
      setSelectedCustomer(customer);
      setShowModal(true);
    }
  };

  // Edit selected customer
  const handleEditCustomer = () => {
    if (selectedRows.length === 1) {
      if (!permissions.canEdit) return;
      const customer = customers.find(c => c._id === selectedRows[0]);
      setSelectedCustomer(customer);
      setShowModal(true);
    }
  };

  // Save customer (create or update)
  const handleSaveCustomer = async (formData) => {
    try {
      if (selectedCustomer && !permissions.canEdit) {
        setError('You do not have permission to edit customers.');
        return;
      }

      if (!selectedCustomer && !permissions.canCreate) {
        setError('You do not have permission to add customers.');
        return;
      }

      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      let response;
      if (selectedCustomer) {
        // Update existing customer
        response = await axios.put(
          `${API_BASE_URL}/customers/${selectedCustomer._id}`,
          formData,
          { headers }
        );
        const name = response.data?.name || selectedCustomer.name || 'Customer';
        showInlineNotice(`Customer ${name} updated successfully.`, { scope: 'customers' });
      } else {
        // Create new customer
        response = await axios.post(`${API_BASE_URL}/customers`, formData, { headers });
        const name = response.data?.name || formData.name || 'Customer';
        showInlineNotice(`Customer ${name} created successfully.`, { scope: 'customers' });
      }

      // Refresh the customer list
      await fetchCustomers(pagination.page, activeSearchTerm);
    } catch (err) {
      console.error('Error saving customer:', err);
      showInlineNotice('Failed to save customer.', {
        scope: 'customers',
        type: 'error'
      });
      throw err;
    }
  };

  // Delete customer
  const handleDeleteCustomer = (customer) => {
    if (!permissions.canDelete) return;
    setCustomerToDelete(customer);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_BASE_URL}/customers/${customerToDelete._id}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const name = customerToDelete?.name || 'Customer';
      showInlineNotice(`Customer ${name} deleted successfully.`, { scope: 'customers' });
      // Refresh the customer list
      fetchCustomers(pagination.page, activeSearchTerm);
      setShowDeleteConfirm(false);
      setCustomerToDelete(null);
    } catch (err) {
      console.error('Error deleting customer:', err);
      const errorMsg = err.response?.data?.error || 'Failed to delete customer';
      setError(errorMsg);
      showInlineNotice('Failed to delete customer.', {
        scope: 'customers',
        type: 'error'
      });
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="recent-invoices">
      <div className="recent-invoices-header">
        <h1>Customers <span className="text-muted">({pagination.total})</span></h1>
      </div>
      {error && <div className="recent-error">{error}</div>}
      <InlineNotice scope="customers" />
      <div className="recent-invoices-list">
        <div className="recent-invoices-table-header">
          <div className="recent-invoices-actions" style={{ justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={handleReload}
                disabled={loading}
                className="recent-invoices-btn recent-invoices-btn-reload"
              >
                {loading ? (
                  <>
                    <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/><path d="M11 1a9 9 0 0 1 9 9"/></svg>
                    Loading...
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                    Reload
                  </>
                )}
              </button>
              <button 
                onClick={handleViewCustomer}
                disabled={selectedRows.length !== 1}
                className="recent-invoices-btn recent-invoices-btn-view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                View
              </button>
              {permissions.canEdit && (
                <button 
                  onClick={handleEditCustomer}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-copy"
                  title="Edit selected customer"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                  Edit
                </button>
              )}
              {permissions.canDelete && (
                <button 
                  onClick={handleDeleteCustomer}
                  disabled={selectedRows.length !== 1}
                  className="recent-invoices-btn recent-invoices-btn-delete"
                  style={{ backgroundColor: '#ef4444', color: 'white' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                  Delete
                </button>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ListSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search by Name, Address, Phone, Email, GST"
                disabled={loading}
                isSearching={loading && Boolean(activeSearchTerm)}
              />
              {permissions.canCreate && (
                <button
                  onClick={handleAddCustomer}
                  style={{
                    backgroundColor: '#3b82f6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              )}
            </div>
          </div>
        </div>
      <div className="table-responsive">
        <Table striped bordered hover>
          <thead>
            <tr>
              <th style={{width: '3%'}}></th>
              <th style={{width: '5%'}}>#</th>
              <th onClick={() => handleSort('name')} style={{width: '18%', cursor: 'pointer'}}>Name {sortConfig.column === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
              <th style={{width: '28%'}}>Address</th>
              <th style={{width: '12%'}}>Phone</th>
              <th style={{width: '12%'}}>GST</th>
              <th style={{width: '15%'}}>Email</th>
              <th onClick={() => handleSort('relationship')} style={{width: '10%', cursor: 'pointer'}}>Relation {sortConfig.column === 'relationship' && (sortConfig.direction === 'asc' ? '▲' : '▼')}</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((customer, index) => (
              <tr 
                key={customer._id}
                className={selectedRows.includes(customer._id) ? 'selected' : ''}
              >
                <td>
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(customer._id)}
                    onChange={() => handleCheckboxChange(customer._id)}
                  />
                </td>
                <td>{(pagination.page - 1) * pagination.limit + index + 1}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal'}}>{customer.name}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal'}}>{customer.address || '-'}</td>
                <td style={{fontSize: '0.85rem'}}>{customer.phone || '-'}</td>
                <td style={{fontSize: '0.85rem'}}>{customer.gst || '-'}</td>
                <td style={{wordWrap: 'break-word', whiteSpace: 'normal', fontSize: '0.85rem'}}>{customer.email || '-'}</td>
                <td style={{fontSize: '0.8rem'}}>{customer.relationship || 'CUSTOMER'}</td>
              </tr>
            ))}
            {customers.length === 0 && (
              <tr>
                <td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#6b7280' }}>
                  {activeSearchTerm ? 'No customers match your search.' : 'No customers found.'}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {pagination.pages > 1 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(1)}
            />
            <Pagination.Prev
              disabled={pagination.page === 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            />
            
            {[...Array(pagination.pages)].map((_, index) => (
              <Pagination.Item
                key={index + 1}
                active={pagination.page === index + 1}
                onClick={() => handlePageChange(index + 1)}
              >
                {index + 1}
              </Pagination.Item>
            ))}
            
            <Pagination.Next
              disabled={pagination.page === pagination.pages}
              onClick={() => handlePageChange(pagination.page + 1)}
            />
            <Pagination.Last
              disabled={pagination.page === pagination.pages}
              onClick={() => handlePageChange(pagination.pages)}
            />
          </Pagination>
        </div>
      )}
      </div>

      {/* Customer Modal */}
      <CustomerModal
        show={showModal}
        onHide={() => setShowModal(false)}
        customer={selectedCustomer}
        onSave={handleSaveCustomer}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title="Delete Customer"
        message={`Are you sure you want to delete "${customerToDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default CustomerListPage;